import React from 'react';
import dollars from '../../dollar-format';
import {Link} from "react-router";
import AgentPipelineDetails from "../AgentPipelineDetails/AgentPipelineDetails";
import {executeQuery} from "../../requests/executeQuery";
import {renderPrintFooter} from "../../utils";
import {STRIVE_AGENT_PIPELINE, OA_AGENT_PIPELINE, OLD_STRIVE_AGENT_PIPELINE} from '../../consts/queries';
import {OVERFLOW_TABLE_WIDTH} from '../../consts/tables';
import {agentPipelineChartOptions} from "../AgentPipeline/chartOptions";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import highcharts3D from 'highcharts/highcharts-3d.src';
import statusColors from '../../status-colors';

highcharts3D(Highcharts);
Highcharts.setOptions({
    lang: {
        decimalPoint: '.',
        thousandsSep: ',',
    }
});

const OAKey = 'Outside Agent';
const priorKey = 'Prior Agents';
const totalKey = 'Total';
const striveTotalKey = 'STRIVE Total';
const threshold = 1000000;

const columns = [{
    label: 'Agent',
    key: 'key',
}, {
    label: '# of Active Listings',
    printLabel: '# of Listings',
    key: 'activeCount',
    includedKeys: [OAKey, priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'Total Price',
    key: 'activePrice',
    includedKeys: [OAKey, priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Potential Fee',
    key: 'grossPotentialFee',
    includedKeys: [OAKey, priorKey, striveTotalKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['Active'],
}, {
    label: '# of In Negotiation',
    printLabel: '# of In Neg',
    key: 'negCount',
    includedKeys: [priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'In Negotiation',
    key: 'negPrice',
    includedKeys: [priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Fee - In Negotiation',
    printLabel: 'Gross Fee - In Neg',
    key: 'negGrossFee',
    includedKeys: [priorKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['In Negotiation'],
}, {
    label: '# of Under Contract',
    printLabel: '# of UC',
    key: 'ucCount',
    includedKeys: [priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'Under Contract',
    key: 'ucPrice',
    includedKeys: [priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Fee - Under Contract',
    printLabel: 'Gross Fee - UC',
    key: 'ucGrossFee',
    includedKeys: [priorKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['Under Contract'],
}, {
    label: '# Closed YTD',
    key: 'closedCount',
    includedKeys: [priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'Closed',
    key: 'closedPrice',
    includedKeys: [priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Fee - Closed',
    key: 'closedGrossFee',
    includedKeys: [priorKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['Closed'],
}];

const initializeRow = () => {
    return {
        id: 0,
        activeCount: 0,
        activePrice: 0,
		grossPotentialFee: 0,
        negCount: 0,
        negPrice: 0,
        negGrossFee: 0,
        ucCount: 0,
        ucPrice: 0,
        ucGrossFee: 0,
        closedCount: 0,
        closedPrice: 0,
        closedGrossFee: 0,
        currentAgentData: {},
        noDataFound: false,
    }
};

const findAgentRow = (rows, agentEmail) => {
    const keys = Object.keys(rows);
    if (!keys || !keys.length) {
        return {};
    }

    const foundKey = keys.filter(agent => rows[agent].email && agentEmail && rows[agent].email.toLowerCase() === agentEmail.toLowerCase());
    const agentKey = foundKey && foundKey[0];
    return agentKey ? {[agentKey]: rows[agentKey]} : {};
};

const tableHeaderHeight = 48;

export default class AgentPipeline extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            rows: {},
            width: 1400,
            currentAgentData: {},
            height: tableHeaderHeight,
            isPrint: false,
            printBackground: '',
            agentPipelineChartOptions: null,
        };

        this.beforePrint = this.beforePrint.bind(this);
        this.afterPrint = this.afterPrint.bind(this);
        this.updateTableWidth = this.updateTableWidth.bind(this);

        this.tableRef = React.createRef();
    }

    static buildData(stateRows, data, status) {
        const rows = {...stateRows};

        data.map(row => {
            if (!rows[row.AGENT_LAST_NAME]) {
                rows[row.AGENT_LAST_NAME] = initializeRow();
            }
            if (!rows[row.AGENT_LAST_NAME].id) {
                rows[row.AGENT_LAST_NAME].id = row.AGENT_ID;
            }
            if (!rows[row.AGENT_LAST_NAME].email) {
                rows[row.AGENT_LAST_NAME].email = row.AGENT_EMAIL;
            }
            if (!rows[row.AGENT_LAST_NAME].name) {
                rows[row.AGENT_LAST_NAME].name = `${row.AGENT_FIRST_NAME} ${row.AGENT_LAST_NAME}`;
            }

            switch (status) {
                case 'Active': {
                    const listingGrossFee = row.LISTING_GROSS_FEE ? parseFloat(row.LISTING_GROSS_FEE) : 0;
                    const buyingGrossFee = row.BUYING_GROSS_FEE ? parseFloat(row.BUYING_GROSS_FEE) : 0;
                    const totalGrossFee = listingGrossFee + buyingGrossFee;
                    rows[row.AGENT_LAST_NAME].activeCount = parseFloat(row.TOTAL_LISTING_COUNT) + parseFloat(row.TOTAL_BUYING_COUNT);
                    rows[row.AGENT_LAST_NAME].activePrice = parseFloat(row.TOTAL_LIST_PRICE);
                    rows[row.AGENT_LAST_NAME].grossPotentialFee = totalGrossFee;
                    break;
                }
                case 'In Negotiation': {
                    const listingNegGrossFee = row.LISTING_NEGOTIATION_GROSS_FEE ? parseFloat(row.LISTING_NEGOTIATION_GROSS_FEE) : 0;
                    const buyingNegGrossFee = row.BUYING_NEGOTIATION_GROSS_FEE ? parseFloat(row.BUYING_NEGOTIATION_GROSS_FEE) : 0;
                    const totalNegGrossFee = listingNegGrossFee + buyingNegGrossFee;
                    rows[row.AGENT_LAST_NAME].negCount = parseFloat(row.TOTAL_LISTING_COUNT) + parseFloat(row.TOTAL_BUYING_COUNT);
                    rows[row.AGENT_LAST_NAME].negPrice = parseFloat(row.TOTAL_LISTING_NEGOTIATION_PRICE) + parseFloat(row.TOTAL_BUYING_NEGOTIATION_PRICE);
                    rows[row.AGENT_LAST_NAME].negGrossFee = totalNegGrossFee;
                    break;
                }
                case 'Under Contract': {
                    const listingUcGrossFee = row.LISTING_NEGOTIATION_GROSS_FEE ? parseFloat(row.LISTING_NEGOTIATION_GROSS_FEE) : 0;
                    const buyingUcGrossFee = row.BUYING_NEGOTIATION_GROSS_FEE ? parseFloat(row.BUYING_NEGOTIATION_GROSS_FEE) : 0;
                    const totalUcGrossFee = listingUcGrossFee + buyingUcGrossFee;
                    rows[row.AGENT_LAST_NAME].ucCount = parseFloat(row.TOTAL_LISTING_COUNT) + parseFloat(row.TOTAL_BUYING_COUNT);
                    rows[row.AGENT_LAST_NAME].ucPrice = parseFloat(row.TOTAL_LISTING_NEGOTIATION_PRICE) + parseFloat(row.TOTAL_BUYING_NEGOTIATION_PRICE);
                    rows[row.AGENT_LAST_NAME].ucGrossFee = totalUcGrossFee;
                    break;
                }
                case 'Closed': {
                    const listingClosedGrossFee = row.LISTING_CLOSED_GROSS_FEE ? parseFloat(row.LISTING_CLOSED_GROSS_FEE) : 0;
                    const buyingClosedGrossFee = row.BUYING_CLOSED_GROSS_FEE ? parseFloat(row.BUYING_CLOSED_GROSS_FEE) : 0;
                    const totalClosedGrossFee = listingClosedGrossFee + buyingClosedGrossFee;
                    rows[row.AGENT_LAST_NAME].closedCount = parseFloat(row.TOTAL_LISTING_CLOSED_COUNT) + parseFloat(row.TOTAL_BUYING_CLOSED_COUNT);
                    rows[row.AGENT_LAST_NAME].closedPrice = parseFloat(row.TOTAL_LISTING_CLOSED_PRICE) + parseFloat(row.TOTAL_BUYING_CLOSED_PRICE);
                    rows[row.AGENT_LAST_NAME].closedGrossFee = totalClosedGrossFee;
                    break;
                }
            }
        });

        return rows;
    }

    static buildOAData(stateRows, data, status, key) {
        const rows = {...stateRows};

        if (!rows[key]) {
            rows[key] = initializeRow();
        }

        switch (status) {
            case 'In Negotiation': {
                const listingNegGrossFee = data.LISTING_NEGOTIATION_GROSS_FEE ? parseFloat(data.LISTING_NEGOTIATION_GROSS_FEE) : 0;
                const buyingNegGrossFee = data.BUYING_NEGOTIATION_GROSS_FEE ? parseFloat(data.BUYING_NEGOTIATION_GROSS_FEE) : 0;
                const totalNegGrossFee = listingNegGrossFee + buyingNegGrossFee;
                rows[key].negCount = parseFloat(data.TOTAL_LISTING_COUNT) + parseFloat(data.TOTAL_BUYING_COUNT);
                rows[key].negPrice = parseFloat(data.TOTAL_LISTING_NEGOTIATION_PRICE) + parseFloat(data.TOTAL_BUYING_NEGOTIATION_PRICE);
                rows[key].negGrossFee = totalNegGrossFee;
                break;
            }
            case 'Under Contract': {
                const listingNegGrossFee = data.LISTING_NEGOTIATION_GROSS_FEE ? parseFloat(data.LISTING_NEGOTIATION_GROSS_FEE) : 0;
                const buyingNegGrossFee = data.BUYING_NEGOTIATION_GROSS_FEE ? parseFloat(data.BUYING_NEGOTIATION_GROSS_FEE) : 0;
                const totalNegGrossFee = listingNegGrossFee + buyingNegGrossFee;
                rows[key].ucCount = parseFloat(data.TOTAL_LISTING_COUNT) + parseFloat(data.TOTAL_BUYING_COUNT);
                rows[key].ucPrice = parseFloat(data.TOTAL_LISTING_NEGOTIATION_PRICE) + parseFloat(data.TOTAL_BUYING_NEGOTIATION_PRICE);
                rows[key].ucGrossFee = totalNegGrossFee;
                break;
            }
            case 'Closed': {
                const listingClosedGrossFee = data.LISTING_CLOSED_GROSS_FEE ? parseFloat(data.LISTING_CLOSED_GROSS_FEE) : 0;
                const buyingClosedGrossFee = data.BUYING_CLOSED_GROSS_FEE ? parseFloat(data.BUYING_CLOSED_GROSS_FEE) : 0;
                const totalClosedGrossFee = listingClosedGrossFee + buyingClosedGrossFee;
                rows[key].closedCount = parseFloat(data.TOTAL_LISTING_CLOSED_COUNT) + parseFloat(data.TOTAL_BUYING_CLOSED_COUNT);
                rows[key].closedPrice = parseFloat(data.TOTAL_LISTING_CLOSED_PRICE) + parseFloat(data.TOTAL_BUYING_CLOSED_PRICE);
                rows[key].closedGrossFee = totalClosedGrossFee;
                break;
            }
        }

        return rows;
    }

    static buildTotalData(stateRows) {
        const rows = {...stateRows, [totalKey]: initializeRow(), [striveTotalKey]: initializeRow()};

        Object.keys(rows[totalKey]).map(columnKey => {
            if (columnKey !== "id") {
                rows[totalKey][columnKey] = Object.keys(rows)
                    .reduce((previous, key) => previous + (typeof rows[key][columnKey] === 'string' ? parseFloat(rows[key][columnKey]) : rows[key][columnKey]), 0);
            }
        });

        rows[striveTotalKey].negGrossFee = rows[totalKey].negGrossFee - rows[OAKey].negGrossFee;
        rows[striveTotalKey].ucGrossFee = rows[totalKey].ucGrossFee - rows[OAKey].ucGrossFee;
        rows[striveTotalKey].closedGrossFee = rows[totalKey].closedGrossFee - rows[OAKey].closedGrossFee;

        return rows;
    }

    static verifyThreshold(series) {
        for (let index = 0; index < series.length; index++) {
            const thresholdPassed = !!series[index].data.filter(value => value > threshold).length;
            if (thresholdPassed) return true;
        }
        return false;
    }

    static buildChartOptions({rows, isAgent, currentAgentData}) {
        const chartRows = isAgent ? currentAgentData : rows;
        if (chartRows) {
            let stateAgentPipelineChartOptions = {};
            const chartRowKeys = Object.keys(chartRows).filter(key => ![priorKey, totalKey, striveTotalKey].includes(key));
            const chartColumns = columns.filter(column => column.shownInChart);
            stateAgentPipelineChartOptions = agentPipelineChartOptions;
            stateAgentPipelineChartOptions.xAxis.categories = chartRowKeys;

            chartColumns.map((column, index) => {
                stateAgentPipelineChartOptions.series[index] = {
                    name: column.label,
                    color: column.color,
                    data: chartRowKeys.map(key => chartRows[key][column.key]),
                }
            });

            if (isAgent) {
                const thresholdPassed = AgentPipeline.verifyThreshold(stateAgentPipelineChartOptions.series);
                stateAgentPipelineChartOptions.chart.options3d.depth = 60;
                stateAgentPipelineChartOptions.chart.width = 790;
                stateAgentPipelineChartOptions.chart.height = 500;
                stateAgentPipelineChartOptions.yAxis.tickInterval = thresholdPassed ? 500000 : 100000;
                stateAgentPipelineChartOptions.plotOptions.column.depth = 60;
                stateAgentPipelineChartOptions.plotOptions.column.pointWidth = 40;
            }

            return stateAgentPipelineChartOptions;
        }

        return null;
    }

	fetch() {
        Promise.all([
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'Active'}}),
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'In Negotiation'}}),
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'Under Contract'}}),
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'Closed'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'In Negotiation'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'Under Contract'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'Closed'}}),
            executeQuery({queryName: OLD_STRIVE_AGENT_PIPELINE, params: {}}),
        ]).then(res => {
            if (res && res[0] && res[0].success && Array.isArray(res[0].data) && res[0].data.length &&
                res[1] && res[1].success && Array.isArray(res[1].data) && res[1].data.length &&
                res[2] && res[2].success && Array.isArray(res[2].data) && res[2].data.length &&
                res[3] && res[3].success && Array.isArray(res[3].data) && res[3].data.length &&
                res[4] && res[4].success && Array.isArray(res[4].data) && res[4].data.length &&
                res[5] && res[5].success && Array.isArray(res[5].data) && res[5].data.length &&
                res[6] && res[6].success && Array.isArray(res[6].data) && res[6].data.length &&
                res[7] && res[7].success && Array.isArray(res[7].data) && res[7].data.length) {
                const activeData = res[0].data;
                const negData = res[1].data;
                const ucData = res[2].data;
                const closedData = res[3].data;
                let rows = {...this.state.rows};
                rows = AgentPipeline.buildData(rows, activeData, 'Active');
                rows = AgentPipeline.buildData(rows, negData, 'In Negotiation');
                rows = AgentPipeline.buildData(rows, ucData, 'Under Contract');
                rows = AgentPipeline.buildData(rows, closedData, 'Closed');
                rows = AgentPipeline.buildOAData(rows, res[4].data[0], 'In Negotiation', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[5].data[0], 'Under Contract', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[6].data[0], 'Closed', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[7].data[0], 'Closed', priorKey);
                rows = AgentPipeline.buildTotalData(rows);

                const state = {
                    rows,
                };

                if (this.props.isAgent) {
                    const currentAgentData = findAgentRow(rows, this.props.userData.email);
                    if (currentAgentData && Object.keys(currentAgentData).length) {
                        state.currentAgentData = currentAgentData;
                    } else {
                        state.noDataFound = 'No data available';
                    }
                }

                state.agentPipelineChartOptions = AgentPipeline.buildChartOptions({rows, isAgent: this.props.isAgent, currentAgentData: state.currentAgentData});

                this.setState(state, () => {
                    const headerHeight = 40;
                    const headDetailsHeight = 116;
                    const bottomSpaceHeight = 30;
                    let height = window.innerHeight - headerHeight - headDetailsHeight - bottomSpaceHeight;
                    if (height > this.tableRef.current.clientHeight && this.tableRef.current.clientHeight >= tableHeaderHeight) {
                        height = this.tableRef.current.clientHeight + (this.props.isAgent ? 0 : 15);
                    }
                    this.setState({height});
                });
            }
        });
	}

    updateTableWidth() {
        this.setState({width: window.innerWidth - OVERFLOW_TABLE_WIDTH});
    }

    componentDidMount() {
        this.updateTableWidth();
        this.setState({printBackground: `url('${arc.path.media+'Transaction_Background.png'}')`}, this.fetch);
        window.addEventListener('resize', this.updateTableWidth);
        window.onbeforeprint = this.beforePrint;
        window.onafterprint = this.afterPrint;
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateTableWidth);
    }

    beforePrint() {
        this.setState({isPrint: true});
    }

    afterPrint() {
        this.setState({isPrint: false});
    }

    static displayValue({type, value, key, includedKeys, isPrintTable}) {
	    if (!value) {
	        if (includedKeys.includes(key)) {
	            return '-';
            }

	        switch (type) {
                case 'number':
                    return '0.00';
                case 'dollar':
                    return isPrintTable ? '$0' : '$0.00';
            }
        }

	    switch (type) {
            case 'number':
                return Number(value).toFixed(2);
            case 'dollar':
                return isPrintTable ? dollars(parseInt(value)) : dollars(value, true);
        }
    }

    renderTable({rows, cols, isAgent, isPrintTable}) {
        return (
            <table className='table' ref={!isPrintTable && this.tableRef}>
                <thead>
                <tr>
                    {
                        cols.map((column, index) => {
                            const label = isPrintTable && column.printLabel ? column.printLabel : column.label;
                            if (index === 0)
                                return <th key={index} className="AgentPipeline_th border_top border_left" id='AgentPipeline_first_cell'>{label}</th>;
                            return <th key={index} className="AgentPipeline_th border_top">{label}</th>
                        })
                    }
                </tr>
                </thead>
                <tbody>
                {
                    Object.keys(rows).map(key => {
                        if (!rows[key]) return;
                        const extraStyle = key === totalKey ? 'total' : (key === striveTotalKey ? 'strive-total' : '');
                        return (
                            <tr key={key} className="AgentPipeline_tr">
                                {
                                    cols.map((column, index) => {
                                        if (index === 0)
                                            return (
                                                <th key={key+'-'+index} className={`AgentPipeline_th agents border_left ${extraStyle}`}>
                                                    {
                                                        rows[key].id && !isAgent ?
                                                            <Link to={{pathname: `${arc.path.base}agent-pipeline/${rows[key].id}`, state: {id: rows[key].id, name: rows[key].name}}}>{key}</Link>
                                                            :
                                                            key
                                                    }
                                                </th>
                                            );
                                        return (
                                            <td key={key+'-'+index} className={`AgentPipeline_td ${extraStyle}`}>
                                                {AgentPipeline.displayValue({type: column.type, value: rows[key][column.key], key, includedKeys: column.includedKeys, isPrintTable})}
                                            </td>
                                        );
                                    })
                                }
                            </tr>
                        );
                    })
                }
                </tbody>
            </table>
        );
    }

    renderPrintPage({rows, cols, date, isPrint, printBackground}) {
        return (
            <div className='print-container' style={{backgroundImage: isPrint && printBackground}}>
                <div className='AgentPipeline_head_left'>
                    <h3 className='AgentPipeline_h3'>STRIVE Agent Pipeline</h3>
                    <h4 className='AgentPipeline_h4'>{date}</h4>
                </div>
                <div className='table-print-container'>
                    {this.renderTable({rows, cols, isPrintTable: true})}
                </div>
                {renderPrintFooter(isPrint)}
            </div>
        )
    }

	render() {
        const isAgent = this.props.isAgent;
        const {agentPipelineChartOptions, currentAgentData, isPrint, printBackground} = this.state;
		const rows = isAgent ? currentAgentData : this.state.rows;
		const currentAgentKeys = Object.keys(currentAgentData);
		const agentKey = isAgent && currentAgentKeys.length && currentAgentKeys[0];
		const noDataFound = this.state.noDataFound;
		const d = new Date();
		const date = d.getMonth()+1+'/'+d.getDate()+'/'+d.getFullYear();

		return (
			<div className='AgentPipeline'>
                {this.renderPrintPage({rows, cols: columns, date, isPrint, printBackground})}
                <div className='preload-print-background' style={{backgroundImage: printBackground}}/>

                <div className='AgentPipeline_header'>
                    <h3 className='AgentPipeline_h3'>STRIVE Agent Pipeline</h3>
                    <h4 className='AgentPipeline_h4'>{date}</h4>
                </div>
                <div className='AgentPipeline_scroll_container' style={{width: this.state.width, height: this.state.height}}>
                    {this.renderTable({rows, cols: columns, isAgent, isPrintTable: false})}
                </div>
                {agentPipelineChartOptions &&
                    <div className='AgentPipeline_chart'>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={agentPipelineChartOptions}
                        />
                    </div>
                }
                {noDataFound ? <div className='no-data'>{noDataFound}</div> : null}
                {isAgent && agentKey ? <AgentPipelineDetails id={currentAgentData[agentKey].id} name={currentAgentData[agentKey].name} withoutAgentName={true}/> : null}
            </div>
		);
	}
}